/**
 * The API telling a headset its pairing has ended (docs/05, "Unpairing").
 *
 * A headset that is unlinked during the day drops the school's data at once,
 * not at its next start (docs/06, "What a headset keeps after unpairing"). It
 * may only do that on an answer that cannot be anything else: a 401 from a
 * captive portal, a proxy, or a person's expired sign-in must never wipe a
 * headset that is properly paired. So the API says it in words, the same
 * words on both channels:
 *
 * - HTTP: `401 {"error":"unauthorized","code":"device-revoked"}`, only when a
 *   device token was presented and no headset holds it (the row was deleted:
 *   unlinked, or the school removed). The lookup succeeded to say so; a
 *   database that is away is a 500, never this.
 * - WebSocket: an `error` frame with that code, then a close with
 *   `DEVICE_REVOKED_CLOSE`, when an admin unlinks a headset that is connected.
 *
 * Shared vocabulary between the headset and the API, like the lesson format.
 */
export const DEVICE_REVOKED = 'device-revoked';
/** The socket close code for a headset unlinked while connected; 4001 and 4004 are the hub's others. */
export const DEVICE_REVOKED_CLOSE = 4010;
/** Whether a 401's body is the API saying no headset holds this token. */
export function isDeviceRevokedAnswer(body) {
    return (typeof body === 'object' &&
        body !== null &&
        body['code'] === DEVICE_REVOKED);
}
/**
 * Whether a bearer token has the shape of a headset's rather than a person's.
 *
 * A device token is exactly what pairing hands out: 32 random bytes in
 * base64url, 43 characters (services/api pairing.ts, whose test holds it to
 * this). A person's token is a JWT, three parts joined by dots, and may be
 * expired or for the wrong audience; only a device token can be "no headset
 * holds this".
 */
export function looksLikeDeviceToken(token) {
    return /^[A-Za-z0-9_-]{43}$/.test(token);
}
//# sourceMappingURL=device-revoked.js.map